import { takeEvery, delay } from 'redux-saga';
import { call, put, take, select } from 'redux-saga/effects';
import debug from 'debug';
import { parse } from 'query-string';

import * as api from 'api/payments';
import * as actions from 'actions/payments';
import { logUserAction } from 'actions/analytics';
import { FETCH_USER_FLAGS_SUCCESS } from 'actions/featureFlags';
import { restoreAd } from 'actions/editable-ad';
import { RECEIVE_USER_INFO } from 'actions/user';
import { getWgToken } from 'selectors/auth';

const log = debug('sagas:payments');

const MAX_POLL_TIME_SECONDS = 30;
// Note: if you want to increase/decrease how often we poll, change this value
const POLL_INTERVAL_SECONDS = 5;
export const POLL_INTERVAL_MS = POLL_INTERVAL_SECONDS * 1000;
export const MAX_POLL_ATTEMPTS = parseInt(MAX_POLL_TIME_SECONDS / POLL_INTERVAL_SECONDS, 10);

export function* waitToFetchPaymentInfo() {
  let groups = null;
  let flags = null;

  while (groups === null || flags === null) {
    const result = yield take([FETCH_USER_FLAGS_SUCCESS, RECEIVE_USER_INFO]);
    switch (result.type) {
      case FETCH_USER_FLAGS_SUCCESS:
        flags = result.flags;
        break;
      case RECEIVE_USER_INFO:
        groups = result.user.groups;
        break;
    }
  }

  yield call(fetchPaymentInfo);
  yield put(actions.paymentInfoFetched());
}

export function* fetchPaymentInfo() {
  yield put(actions.disallowFetchRetry());
  yield put(actions.fetchingPaymentInfo());

  yield call(handleAdyenFetch);

  yield put(actions.fetchingPaymentInfoStopped());
}

// Adyen
export function getAdyenAuthResult(windowObj) {
  const params = getURLParams(windowObj);
  return params.authResult;
}

function getURLParams(windowObj) {
  const params = windowObj.location.search
    ? parse(windowObj.location.search)
    : {};
  // Stringify to avoid debug#log error in prod
  log('parsed url params: ' + JSON.stringify(params));
  return params;
}

export function* handleAdyenFetch(windowObj = window) {
  const authResult = yield call(getAdyenAuthResult, windowObj);
  if (!!authResult) {
    log('user is coming back from the adyen hpp');
    yield call(handleAdyenAuthResult, authResult);
    return;
  }

  log('user is NOT coming back from the adyen hpp');

  try {
    yield call(fetchAdyenRecurringDetail);
  } catch (e) {
    if (e instanceof api.AdyenRecurringDetailNotificationPendingError) {
      log('user not coming back from adyen and seeing a 204');
    }
  }
}

export function* handleAdyenAuthResult(authResult) {
  if (authResult === 'AUTHORISED' || authResult === 'REFUSED') {
    log('turning on adyen polling indication');
    try {
      yield call(linkCardToAdyenUser);
    } catch (e) {
      log('failed to link card to user');
      yield put(actions.allowFetchRetry());
      // cannot get adyen details unless card is linked
      return;
    }
    yield call(pollForAdyenRecurringDetails);
    put(
      logUserAction({
        category: 'create_ad_flow',
        label: 'adyen_auth_completed',
      })
    );
  } else {
    log('in a failed authResult state: ' + authResult);
    put(
      logUserAction({
        category: 'create_ad_flow',
        label: 'adyen_auth_errored',
      })
    );
    yield put(
      actions.receivedRecurringDetailFailed(
        new Error(api.DEFAULT_ADYEN_FETCH_ERROR)
      )
    );
  }
}

export function* linkCardToAdyenUser(windowObj = window) {
  log('linking card to user');
  const params = getURLParams(windowObj);
  const wgToken = yield select(getWgToken);
  yield call(poll, api.linkCardToAdyenUser, wgToken, params);
}

export function* pollForAdyenRecurringDetails() {
  try {
    yield call(poll, fetchAdyenRecurringDetail);
  } catch (e) {
    log('failed to get card details');
    yield put(actions.allowFetchRetry());
  }
}

export function* poll(method, ...methodArgs) {
  const methodName = method.name || 'DAMN YOU IE';
  log(`initiating poll to backend, ${methodName}`);
  log('MAX polling attempts: ', MAX_POLL_ATTEMPTS);

  for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
    try {
      yield call(method, ...methodArgs);
      log(`${methodName} call succeeded.`);
      return;
    } catch (e) {
      log(`awaiting response for ${methodName}; should retry: ${e.message}`);
      // No need to delay on the last failed iteration
      if (i !== MAX_POLL_ATTEMPTS - 1) {
        yield call(delay, POLL_INTERVAL_MS);
      }
    }
  }

  log(`exceeded the maximum number of ${methodName} poll attempts`);
  throw new Error(`exceeded the maximum number of ${methodName} poll attempts`);
}

export function* fetchAdyenRecurringDetail() {
  const wgToken = yield select(getWgToken);

  try {
    log('fetching adyen recurring detail');
    const recurringDetail = yield call(api.getAdyenRecurringDetails, wgToken);

    log('recurring detail information: ', recurringDetail);
    yield put(actions.receivedRecurringDetail(recurringDetail));
  } catch (e) {
    if (e instanceof api.AdyenRecurringDetailNotFoundError) {
      log('recurring detail not found');
    } else if (e instanceof api.AdyenRecurringDetailNotificationPendingError) {
      log('recurring detail notification pending');
      throw e;
    } else if (e instanceof api.AdyenRecurringDetailError) {
      log('recurring detail error: ' + e.message);
      yield put(actions.receivedRecurringDetailFailed(e));
    } else {
      log(`Unable to retrieve recurring detail information: ${e.message}`);
      yield put(actions.receivedRecurringDetailFailedSystemError(e));
    }
  }
}

export function* watchFetchRecurringDetail() {
  log('watching for a FETCH_ADYEN_RECURRING_DETAIL event');
  yield call(takeEvery, actions.FETCH_ADYEN_RECURRING_DETAIL, fetchPaymentInfo);
}

export function* checkForHPPAdRestore(windowObj = window) {
  const authResult = yield call(getAdyenAuthResult, windowObj);

  if (!!authResult) {
    log('user came back from the adyen hpp; restoring saved ad');
    yield put(restoreAd());
  }
}



// WEBPACK FOOTER //
// ./src/middleware/sagas/payments.js